// src/domains/sales/deals/view/sales-deal-view.jsx

import { CardLoader } from 'components/organisms'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Collapse,
  Container,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Table
} from 'reactstrap'
import { useEffect, useState } from 'react'
import { PAYMENT_METHOD, DEAL_STATE } from '../constants'
import QRCode from 'react-qr-code'
import StringUtil from 'utils/string-util'
import TimeUtil from 'utils/time-util'
import { PAYMENT_STATE } from 'domains/finance/payments/constants'
import TableUtil from 'utils/table-util'
import { printInvoice, emailInvoice } from 'utils/invoice-util'

// NEW: extracted component (keeps form logic isolated)
import UploadTransferReceiptCard from './UploadTransferReceiptCard'

const propTypes = {
  deal: PropTypes.object,
  fetchDeal: PropTypes.func,
  cancelDeal: PropTypes.func,
  fetchHistoryList: PropTypes.func,
  historyList: PropTypes.array,
  fetchHistoryDetail: PropTypes.func,
  historyDetail: PropTypes.object,
  clearHistoryDetail: PropTypes.func,
}

const defaultValues = {
  deal: {},
  fetchDeal: () => {},
  cancelDeal: () => {},
  fetchHistoryList: () => {},
  historyList: [],
  fetchHistoryDetail: () => {},
  historyDetail: {},
  clearHistoryDetail: () => {},
}

/**
 * Displaying detail of Deal in table view
 * @param {Object} deal
 */
const SalesDealsView = ({
  deal,
  fetchDeal,
  cancelDeal,
  fetchHistoryList,
  historyList,
  fetchHistoryDetail,
  historyDetail,
  clearHistoryDetail,
}) => {
  const [ isDetailOpen, setIsDetailOpen ] = useState(false)
  const [ isHistoryOpen, setIsHistoryOpen ] = useState(false)
  const [ showDifferenceOnly, setShowDifferenceOnly ] = useState(false)

  // Email invoice status UI
  const [ isEmailingInvoice, setIsEmailingInvoice ] = useState(false)
  const [ emailInvoiceResult, setEmailInvoiceResult ] = useState(null) // { color: "success"|"danger", message: string }

  useEffect(() => {
    if(isHistoryOpen){
      fetchHistoryList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isHistoryOpen])

  const tableData = TableUtil.convertHistoryDetailIntoTableData(historyDetail || {})

  const paymentState = deal?.payment?.state
  const paymentDone = [PAYMENT_STATE.CAPTURE, PAYMENT_STATE.SETTLEMENT].includes(paymentState)

  const dealState = deal?.state
  const dealInProgress = !!dealState && ![DEAL_STATE.CANCELLED, DEAL_STATE.FAILED].includes(dealState)

  const isManualTransfer = deal?.payment?.method_type === PAYMENT_METHOD.MANUAL_TRANSFER

  const dealInclusions = []
  if(deal?.include_invoice){
    dealInclusions.push(( <li key="include_invoice">Invoice</li> ))
  }
  if(deal?.include_polaroid){
    dealInclusions.push(( <li key="include_polaroid">Polaroid</li> ))
  }
  if(deal?.include_love_letter){
    dealInclusions.push(( <li key="include_love_letter">Surat Cinta</li> ))
  }

  const handleEmailInvoice = async (e) => {
    setEmailInvoiceResult(null)
    setIsEmailingInvoice(true)
    try {
      const result = await emailInvoice(e, deal)
      setEmailInvoiceResult({
        color: "success",
        message: result?.message || "Invoice email sent successfully."
      })
    } catch (err) {
      setEmailInvoiceResult({
        color: "danger",
        message: err?.message || "Failed to send invoice email."
      })
    } finally {
      setIsEmailingInvoice(false)
    }
  }

  return (
    <>
      <Card>
        <div className='p-3'>
          <div>
            <h2 className='mb-3 text-center'>{ `Summary (#${deal?.id}) - ${deal?.reference_number}` }</h2>
            <Button onClick={fetchDeal} block color="info" className='mb-3'>Refresh Data</Button>

            {
              !paymentDone && dealInProgress && isManualTransfer
              &&
              <div className='text-center text-sm flex-col'>
                <p>Silahkan transfer Final Payment ke BCA dengan no. rek. 4232387878 atas nama PT BERKAH NUSANTARA KAYANA</p>
              </div>
            }

            {/* NEW: Upload receipt card (extracted component) */}
            {
              dealInProgress && !paymentDone && isManualTransfer
              &&
              <UploadTransferReceiptCard
                deal={deal}
                fetchDeal={fetchDeal}
              />
            }

            {
              !paymentDone
              &&
              dealInProgress
              &&
              [PAYMENT_METHOD.MANUAL_TRANSFER, PAYMENT_METHOD.CASH, PAYMENT_METHOD.EDC].includes(deal?.payment?.method_type) === false
              &&
              <div className='text-center text-sm flex-col'>
                <p className='font-weight-bold'>Payment Link</p>
                <p>{ deal?.payment?.redirect_url }</p>
                {
                  deal?.payment?.redirect_url
                  &&
                  <QRCode className='mb-4' value={ deal.payment.redirect_url } />
                }
              </div>
            }

            {/* Cash and EDC should be handled outside system */}
            <Table bordered responsive striped>
              <tbody>
                <tr>
                  <th scope="row" className='text-right'>Final Payment</th>
                  <td>{StringUtil.currencyCase(deal?.final_payment)}</td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>Status</th>
                  <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.state))}</td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>Pembayaran</th>
                  <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.payment?.method_type))}</td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>Kurir</th>
                  <td>
                    {[
                      deal?.shipment?.courier_detail?.logistic?.name,
                      deal?.shipment?.courier_detail?.rate?.name
                    ].filter(str => !!str).join(" - ")}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>Pickup Date</th>
                  <td>{deal?.pickup_at ? TimeUtil.format(deal.pickup_at, 'DD-MM-YYYY') : "-"}</td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>Down Payment</th>
                  <td>{deal?.down_payment ? StringUtil.currencyCase(deal.down_payment) : "-"}</td>
                </tr>
              </tbody>
            </Table>

            {
              dealInProgress
              &&
              <Button onClick={cancelDeal} block color="danger">Cancel</Button>
            }
          </div>
        </div>
      </Card>

      <Card>
        <div className='p-3'>
          <Button
            color="success"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            onClick={(e) => printInvoice(e, deal)}
          >
            Print Invoice
          </Button>

          <Button
            color="primary"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            onClick={handleEmailInvoice}
            disabled={isEmailingInvoice}
          >
            {isEmailingInvoice ? "Sending Invoice..." : "Email Invoice to Customer"}
          </Button>

          {
            emailInvoiceResult
            &&
            <Alert color={emailInvoiceResult.color} className="mb-3">
              {emailInvoiceResult.message}
            </Alert>
          }

          <Button
            color="success"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            href={`${process.env.REACT_APP_CUSTOMER_WEB_URL}/ops/create-invoice?refNum=${deal?.reference_number}`}
            target="_blank" rel="noopener noreferrer"
          >
            Create Invoice
          </Button>

          <Button
            color="info"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            onClick={() => setIsDetailOpen(!isDetailOpen)}
          >
            Lihat Detail
          </Button>

          <Collapse isOpen={isDetailOpen}>
            <Card>
              <CardBody>
                <h2 className='mb-3 text-center'>Detail</h2>
                <ListGroup className='text-sm'>
                  <ListGroupItem>
                    Voucher: {deal?.voucher_code}
                  </ListGroupItem>
                  <ListGroupItem>
                    Sales Method: {deal?.sales_method}
                  </ListGroupItem>
                  <ListGroupItem>
                    Nama Sales di Invoice: {deal?.sales_name_in_invoice}
                  </ListGroupItem>
                  <ListGroupItem>
                    Deal By: {deal?.deal_by}
                  </ListGroupItem>
                  <ListGroupItem>
                    Assist By: {deal?.assist_by}
                  </ListGroupItem>
                  <ListGroupItem>
                    Catatan Order:<br/>{deal?.note}
                  </ListGroupItem>
                  {
                    dealInclusions.length > 0
                    &&
                    <ListGroupItem>
                      Include:
                      <ul style={{paddingLeft: "14px"}}>
                        {dealInclusions}
                      </ul>
                    </ListGroupItem>
                  }
                </ListGroup>

                <h2 className='my-3 text-center'>Produk yang Dibeli</h2>
                <div>
                  {(deal?.cart?.cart_items || []).map((cart_item) => {
                    const inclusionsListItems = []

                    if(cart_item?.include_authenticity_check){
                      inclusionsListItems.push((
                        <li key={`auth_${cart_item?.id || cart_item?.product?.sku}`}>
                          Authentication
                          <ul style={{paddingLeft: "14px"}}>
                            {[cart_item?.authenticator, cart_item?.authenticity_check_note]
                              .filter(Boolean)
                              .map((str, i) => <li key={`auth_note_${cart_item?.id || cart_item?.product?.sku}_${i}`}>{str}</li>)
                            }
                          </ul>
                        </li>
                      ))
                    }

                    const imgSrc = cart_item?.product?.summary_index?.direct_photos?.[0]
                    const itemKey = cart_item?.id || cart_item?.product?.sku

                    return (
                      <Container className="my-4" key={itemKey}>
                        <Row className="align-items-center">
                          <Col xs="12" md="6" className="text-center mb-3 mb-md-0">
                            {imgSrc ? (
                              <img
                                style={{width: '100%'}}
                                alt={cart_item?.product?.name}
                                src={imgSrc}
                              />
                            ) : (
                              <div className="text-muted">No image</div>
                            )}
                          </Col>

                          <Col xs="12" md="6">
                            <p>{cart_item?.product?.name}</p>
                            <ListGroup className='text-sm'>
                              <ListGroupItem>
                                SKU: {cart_item?.product?.sku}
                              </ListGroupItem>
                              <ListGroupItem>
                                Harga: {StringUtil.currencyCase(cart_item?.price)}
                              </ListGroupItem>
                              {
                                inclusionsListItems.length > 0
                                &&
                                <ListGroupItem>
                                  Include:
                                  <ul style={{paddingLeft: "14px"}}>
                                    {inclusionsListItems}
                                  </ul>
                                </ListGroupItem>
                              }
                              {
                                cart_item?.note
                                &&
                                <ListGroupItem>
                                  Catatan Item:<br/>{cart_item.note}
                                </ListGroupItem>
                              }
                            </ListGroup>
                          </Col>
                        </Row>
                      </Container>
                    )
                  })}
                </div>
              </CardBody>
            </Card>
          </Collapse>

          <Button
            color="info"
            style={{marginLeft: 0, marginBottom: "8px", width: "100%"}}
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
          >
            Lihat History
          </Button>

          <Collapse isOpen={isHistoryOpen}>
            <Card>
              <CardBody>
                <h2 className='mb-3 text-center'>History</h2>

                <ListGroup>
                  {(historyList || []).map((history) => {
                    const isActive = history?.id === historyDetail?.id
                    return (
                      <div key={history?.id}>
                        <ListGroupItem
                          action
                          active={isActive}
                          tag="button"
                          onClick={() => {
                            setShowDifferenceOnly(false)
                            if(isActive){
                              clearHistoryDetail()
                            }else{
                              fetchHistoryDetail(history.id)
                            }
                          }}
                        >
                          {TimeUtil.format(history.created_at, 'YYYY-MM-DD HH:mm:ss')}: ({history.done_by}) {history.action_type.replaceAll("_", " ")}
                        </ListGroupItem>

                        {
                          isActive
                          &&
                          <>
                            <Label className='text-gray-light text-nowrap mt-2'>
                              Lihat field yang berubah saja
                            </Label>
                            <Label className="custom-toggle mb-4">
                              <span></span>
                              <input
                                type="checkbox"
                                value={showDifferenceOnly}
                                onClick={() => setShowDifferenceOnly(!showDifferenceOnly)}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle text-success"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </Label>

                            <Table striped>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Before</th>
                                  <th>After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableData
                                  .filter(o => {
                                    if(showDifferenceOnly){
                                      return !o.sameValue
                                    }
                                    return true
                                  })
                                  .map((o, idx) => (
                                    <tr key={`${o.fieldName}_${idx}`}>
                                      <th scope="row">{o.fieldName}</th>
                                      <td>{o.before}</td>
                                      <td>{o.after}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </>
                        }
                      </div>
                    )
                  })}
                </ListGroup>
              </CardBody>
            </Card>
          </Collapse>
        </div>
      </Card>
    </>
  )
}

SalesDealsView.propTypes    = propTypes
SalesDealsView.defaultProps = defaultValues

export default withLoader(SalesDealsView, CardLoader)
